/* eslint-disable react/prop-types */
import React from 'react';
import pluralize from 'pluralize';

import { CHANNEL_NAME } from './channels';
import { MEDIA_TYPE } from './studio';

export const MODALS = {
  AFFILIATE: {
    PAYOUT_METHOD: 'affiliates_payout_method',
    SURVEY: {
      APPLICATION: 'affiliates_survey_application',
      START: 'affiliates_survey_start',
      SUCCESS: 'affiliates_survey_success',
    },
  },
  ALT_TEXT: 'alt_text',
  AVATAR: 'avatar',
  BILLING: {
    MANAGE: 'manage',
    SUCCESS: 'success',
  },
  CONFIRMATIONS: {
    CANCEL: 'cancel',
    CHANGE_CATEGORY: 'change_category',
    CLOSE_PHOTO_EDITOR: 'close_photo_editor',
    DELETE: 'delete',
    LEAVE_PHOTO_EDITOR: 'leave_photo_editor',
    PUBLISH: 'publish',
    UPDATE_EXISTING: 'update_existing',
  },
  COPY_CONTENT: 'copy_content',
  COPY_LISTINGS: 'copy_listings',
  CREATE_LISTING: 'create_listing',
  CSV: {
    EXPORT_LISTINGS: 'csv_export_listings',
    IMPORT_LISTINGS: {
      LOAD_FILE: 'csv_import_listings_load_file',
      MAP_FIELDS: 'csv_import_listings_map_fields',
    },
    MANAGE_SETTINGS: 'csv_export_listings',
  },
  MERGE_LISTINGS: 'merge_listings',
  PHOTO: {
    EDITOR: 'photo_editor',
  },
  PROFILES: {
    CANCEL: 'profiles_cancel',
    CREATE: 'profiles_create',
    DELETE: 'profiles_delete',
    LEAVE: 'profiles_leave',
  },
  RESCHEDULE_PUBLISH: 'reschedule_publish',
  SCHEDULE_PUBLISH: 'schedule_publish',
  SCHEDULE_UPDATES: 'schedule_updates',
  STUDIO: {
    LIBRARY: {
      CREATE_FOLDER: 'studio_library_create_folder',
      DELETE_FOLDER: 'studio_library_delete_folder',
      DELETE_PHOTOS: 'studio_library_delete_photos',
      DELETE_VIDEOS: 'studio_library_delete_videos',
      MOVE_INTO_NEW_FOLDER: 'studio_library_move_into_new_folder',
      RENAME_FOLDER: 'studio_library_rename_folder',
    },
    SELECT_MEDIA: 'studio_select_media',
  },
  SYNC: {
    COPY: 'sync_copy',
    PUBLISH: 'sync_publish',
    REFRESH: 'sync_refresh',
    OPTIMIZING: 'sync_optimizing',
    REFRESH_REQUIRED: 'sync_refresh_required',
    SCHEDULED_PUBLISH: 'sync_scheduled_publish',
    SCHEDULED_UPDATE: 'sync_scheduled_update',
  },
  UPDATE_TOKEN: 'update_token',
  VIDEO: {
    EXTERNAL: 'video_external',
    PREVIEW: 'video_preview',
  },
  WAIT_LIST: 'wait_list',
};

export const TITLES = {
  [MODALS.AFFILIATE.PAYOUT_METHOD]: 'Payout Account',
  [MODALS.AFFILIATE.SURVEY.APPLICATION]: 'Affiliate application',
  [MODALS.AFFILIATE.SURVEY.START]: 'Vela Affiliates',
  [MODALS.AFFILIATE.SURVEY.SUCCESS]: 'Application Submitted',
  [MODALS.ALT_TEXT]: 'Edit alt text',
  [MODALS.AVATAR]: 'Crop your photo',
  [MODALS.CONFIRMATIONS.CANCEL]: 'Cancel updates?',
  [MODALS.CONFIRMATIONS.CHANGE_CATEGORY]: 'Changing category will reset attributes',
  [MODALS.CONFIRMATIONS.CLOSE_PHOTO_EDITOR]: 'Close photo editor?',
  [MODALS.CONFIRMATIONS.DELETE]: 'Delete listings?',
  [MODALS.CONFIRMATIONS.LEAVE_PHOTO_EDITOR]: 'Leave photo editor?',
  [MODALS.CONFIRMATIONS.PUBLISH]: 'Publish a new listing',
  [MODALS.CONFIRMATIONS.UPDATE_EXISTING]: 'Update existing listings?',
  [MODALS.COPY_CONTENT]: 'Add shop',
  [MODALS.COPY_LISTINGS]: 'Copy listings',
  [MODALS.CSV.IMPORT_LISTINGS.LOAD_FILE]: 'Import CSV',
  [MODALS.CSV.MANAGE_SETTINGS]: 'Manage import settings',
  [MODALS.MERGE_LISTINGS]: 'Merge Listings',
  [MODALS.PROFILES.CANCEL]: 'Cancel editing profile?',
  [MODALS.PROFILES.CREATE]: 'Create Profile',
  [MODALS.PROFILES.DELETE]: 'Delete profile?',
  [MODALS.PROFILES.LEAVE]: 'Cancel editing profile and leave?',
  [MODALS.RESCHEDULE_PUBLISH]: 'Reschedule',
  [MODALS.SCHEDULE_PUBLISH]: 'Schedule listings',
  [MODALS.SCHEDULE_UPDATES]: 'Schedule update',
  [MODALS.STUDIO.SELECT_MEDIA]: {
    [MEDIA_TYPE.PHOTOS]: 'Select photos',
    [MEDIA_TYPE.VIDEOS]: 'Select videos',
  },
  [MODALS.STUDIO.LIBRARY.CREATE_FOLDER]: 'New folder',
  [MODALS.STUDIO.LIBRARY.MOVE_INTO_NEW_FOLDER]: 'New folder for images',
  [MODALS.STUDIO.LIBRARY.DELETE_FOLDER]: 'Delete folder?',
  [MODALS.STUDIO.LIBRARY.DELETE_PHOTOS]: 'Delete photos?',
  [MODALS.STUDIO.LIBRARY.DELETE_VIDEOS]: 'Delete videos?',
  [MODALS.STUDIO.LIBRARY.RENAME_FOLDER]: 'Rename folder',
  [MODALS.SYNC.COPY]: 'Copying listings',
  [MODALS.SYNC.OPTIMIZING]: 'Optimizing listings',
  [MODALS.SYNC.PUBLISH]: 'Publishing listings',
  [MODALS.SYNC.REFRESH]: 'Refreshing shop',
  [MODALS.SYNC.REFRESH_REQUIRED]: 'Refresh shop',
  [MODALS.SYNC.SCHEDULED_PUBLISH]: 'Publishing listing',
  [MODALS.SYNC.SCHEDULED_UPDATE]: 'Scheduled Update',
  [MODALS.UPDATE_TOKEN]: 'New features alert',
  [MODALS.VIDEO.EXTERNAL]: 'Add file from URL',
  [MODALS.WAIT_LIST]: 'Channel waitlist',
};

export const DESCRIPTIONS = {
  [MODALS.AFFILIATE.SURVEY.SUCCESS]: 'Thanks for your interest in becoming an Affiliate! We’ll review your application and be in touch shortly.',
  [MODALS.AVATAR]: 'Adjust your avatar',
  [MODALS.CONFIRMATIONS.CANCEL]: 'You have updates that have NOT been synced. Closing this page without syncing will cause all updates to be lost.',
  [MODALS.CONFIRMATIONS.CHANGE_CATEGORY]: 'One or more of this listing’s attributes aren’t relevant in the new category, so they’ll be removed. Any variations will remain unchanged.',
  [MODALS.CONFIRMATIONS.CLOSE_PHOTO_EDITOR]: 'You have updates that have NOT been saved. Closing the editor without saving will cause all updates to be lost.',
  [MODALS.CONFIRMATIONS.DELETE]: 'Listings will be deleted in both Vela and the channel and can not be recovered.',
  [MODALS.CONFIRMATIONS.LEAVE_PHOTO_EDITOR]: 'You have updates that have NOT been saved. Leaving the editor without saving will cause all updates to be lost.',
  [MODALS.CONFIRMATIONS.PUBLISH]: [
    'There’s a non-refundable fee of $0.20 for each new listing-by publishing, you agree to pay this fee.',
    'The publication date will reset to today’s date, and the listing will expire four months from today. You can turn off auto-renew anytime in Listings. For more details see Etsy’s Fees & Payments Policy.',
    'Etsy auto-renews every four months at $0.20 per listing, you can change this anytime in Vela or Etsy.',
  ],
  [MODALS.CONFIRMATIONS.UPDATE_EXISTING]: 'These listings match listings already in your shop. Would you like to update the existing listings or publish new listings?',
  [MODALS.COPY_CONTENT]: 'Copy content from an existing shop or use a new listing',
  [MODALS.COPY_LISTINGS]: 'Select “Standard” to copy listings as they exist, or “AI copy” to copy and optimize listings for the selected shop',
  [MODALS.CSV.EXPORT_LISTINGS]: 'Provide the email address where you’d like the CSV file sent once the export is complete.',
  [MODALS.CSV.IMPORT_LISTINGS.LOAD_FILE]: 'You can upload any CSV file, but we recommend using our CSV template for the best results and easy attribute mapping.',
  [MODALS.CSV.IMPORT_LISTINGS.MAP_FIELDS]: 'Importing will not overwrite any existing products that have the same product handle and won’t be published. Review the matching column titles and use the dropdowns to make any changes needed.',
  [MODALS.PROFILES.CANCEL]: 'All unsaved changes will be lost.',
  [MODALS.PROFILES.CREATE]: 'New profiles will be saved and can be edited within Profiles',
  [MODALS.PROFILES.DELETE]: 'Profile will be deleted and can not be recovered. Connected listings will be saved.',
  [MODALS.PROFILES.LEAVE]: 'All unsaved changes will be lost.',
  [MODALS.RESCHEDULE_PUBLISH]: 'Select date and time when these listings should be published',
  [MODALS.SCHEDULE_PUBLISH]: 'Select date and time when these listings should be published',
  [MODALS.SCHEDULE_UPDATES]: 'Schedule a timed update for selected attributes',
  [MODALS.STUDIO.LIBRARY.DELETE_FOLDER]: 'Photos inside the folder will be deleted and can not be recovered.',
  [MODALS.STUDIO.LIBRARY.DELETE_PHOTOS]: 'Photos will be deleted in both Vela and the channel and can not be recovered.',
  [MODALS.STUDIO.LIBRARY.DELETE_VIDEOS]: 'Videos will be deleted in both Vela and the channel and can not be recovered.',
  [MODALS.STUDIO.SELECT_MEDIA]: {
    [MEDIA_TYPE.PHOTOS]: 'You can multiselect photos from your library',
    [MEDIA_TYPE.VIDEOS]: 'You can multiselect videos from your library',
  },
  [MODALS.SYNC.COPY]: 'The time this takes will depend on the number of listings copied',
  [MODALS.SYNC.PUBLISH]: 'The time this takes will depend on the number of listings to be published',
  [MODALS.SYNC.REFRESH]: 'The time this takes will depend on the number of listings in your shop',
  [MODALS.SYNC.REFRESH_REQUIRED]({ channel, daysFromSync }) {
    return (
      <React.Fragment key={MODALS.SYNC.REFRESH_REQUIRED}>
        It looks like your shop was last refreshed <strong>{pluralize('day', daysFromSync, true)}</strong> ago.
        Click “Refresh shop” to import the latest from {CHANNEL_NAME[channel]} and ensure Vela is up to date.
      </React.Fragment>
    );
  },
  [MODALS.SYNC.SCHEDULED_PUBLISH]: (
    <React.Fragment key={MODALS.SYNC.SCHEDULED_PUBLISH}>
      A scheduled listing you created is being published. The time it<br />
      takes will depend on the number of listings to be published.
    </React.Fragment>
  ),
  [MODALS.SYNC.SCHEDULED_UPDATE]: (
    <React.Fragment key={MODALS.SYNC.SCHEDULED_UPDATE}>
      A scheduled update you created is being implemented.<br />
      The time it takes will depend on the number<br />
      of listings included in the update.
    </React.Fragment>
  ),
  [MODALS.UPDATE_TOKEN]: 'In order to access new features that will be launching soon, you’ll need to update the app in Shopify.',
  [MODALS.VIDEO.EXTERNAL]: 'Youtube or Vimeo URL',
  [MODALS.WAIT_LIST]: 'Select the channels where you sell (or would like to) to receive first access as they are launched in Vela',
};
